import { Button } from '@components/button';
import { IconJourneyApp } from '@components/icons/journey-app';
import { useSidebar } from '@components/sidebar';
import { useScopedTranslate } from '@locale/translate';

import { useJobTriggerCount } from './hooks';
import { JobStageInfo, TriggersSidebar, TriggersSidebarProps } from './triggers-sidebar';

type TriggersButtonProps = {
  jobId: number;
  jobStages: JobStageInfo[];
  isAdmin: boolean;
};

export const TriggersButton = ({ jobId, jobStages, isAdmin }: TriggersButtonProps) => {
  const { t } = useScopedTranslate(locale);
  const countQuery = useJobTriggerCount(jobId);
  const triggerCount = countQuery.data?.count ?? 0;
  // The counter reads 0 on a job with no triggers rather than disappearing, per
  // the acceptance criterion ("a configured-trigger counter (default 0)").
  // Gated on the query having resolved so the label does not paint 0 and then
  // jump to the real count on first load.
  const isCountKnown = countQuery.data !== undefined;

  const showSidebar = useSidebar(TriggersSidebar, {
    variant: 'REGULAR',
    hasBackdrop: true,
  });

  const handleClick = () => {
    const sidebarProps: TriggersSidebarProps = { jobId, jobStages, isAdmin };
    showSidebar(sidebarProps);
  };

  return (
    <Button
      dataCy="journeys-trigger-button"
      variant="secondary"
      iconLeft={<IconJourneyApp />}
      text={isCountKnown ? String(triggerCount) : undefined}
      tooltip={t('tooltipDescription')}
      aria-label={t('tooltip')}
      onClick={handleClick}
    />
  );
};

const locale = {
  en: {
    tooltip: 'Journey triggers',
    // The visible label is just the trigger count: aria-label above stays the
    // short accessible name; this is the longer tooltip text, so a
    // screen-reader user does not hear the identical short label twice (once
    // as the name, once again as the tooltip on focus).
    tooltipDescription: 'Open journey triggers',
  },
  is: {
    tooltip: 'Sjálfvirk ræsing Journey-ferla',
    tooltipDescription: 'Opna stillingar fyrir sjálfvirka ræsingu Journey-ferla',
  },
};
